import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItemPrice from 'LuxKit/List/ListItemPrice'
import { OfferPageActions, takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useCallback, useId } from 'react'
import * as React from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { getMultiRoomDurationOptions } from 'selectors/offerPage/multipleRoomBookingSelectors'
import RadioButton from 'LuxKit/Radio/RadioButton'
import type { OfferDurationOption } from '../DurationTabs/OfferDurationTabs'
import noop from 'lib/function/noop'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Label from 'LuxKit/Label'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import Pill from 'LuxKit/Pill'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { filterCharterUnbundledDurationOptions, getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

export function getOptionLabel(option: OfferDurationOption, roomCount: number = 1, saleUnit: string = 'room', intl?: IntlShape) {
  const currentIntl = intl ?? getCurrentIntl()

  if (option.soldOut) {
    return currentIntl.formatMessage({
      id: 'F5ByZ57VadSfU9ZTBFoP',
      defaultMessage: 'Sold out',
      description: 'Label shown when a duration option is sold out on offer page',
    })
  } else if (option.lowestPrice === undefined) {
    return undefined
  } else {
    if (roomCount > 1) {
      return currentIntl.formatMessage(
        {
          id: 'tempCountLabel000001',
          defaultMessage: '{saleUnitCount} from',
          description: 'Pricing label with pluralised sale unit count followed by from in the duration filter option',
        },
        { saleUnitCount: formatSaleUnitCount(currentIntl, saleUnit, roomCount) },
      )
    }
    return currentIntl.formatMessage({
      id: 'WXYJI-HcfG0ki3QR-ekH',
      defaultMessage: 'From',
      description: 'Pricing label prefix before duration option price on the offer detail page',
    })
  }
}

export function getSubtitleLabel(option: OfferDurationOption, intl?: IntlShape) {
  if (isLPCTacticalEnabled() && option.hasTactical) {
    return <LabelGroup>
      <Label
        startIcon={<SolidFireIcon colour="urgency" size="XS" />}
        variant="critical"
        kind="plain"
      >
        {intl?.formatMessage({
          id: 'gR2iWPJUZECvhkY37rs_',
          defaultMessage: 'Limited Time Special',
          description: 'Label for a tactical limited-time promotion on a duration option',
        })}
      </Label>
      {(option.discountPercent ?? 0) > 0 && <Pill kind="primary" variant="success">{intl?.formatMessage(
        {
          id: 'kbAgQU8wDQmuFI9EMDJa',
          defaultMessage: '-{discount}%',
          description: 'Discount percentage shown on duration option on the offer detail page',
        },
        { discount: option.discountPercent },
      )}
      </Pill>}
    </LabelGroup>
  }

  return undefined
}

interface Props {
  durationOptions: Array<OfferDurationOption>
  selectedDuration: number
  onClose?: () => void
  bundledWithFlightsOnly: boolean
  currentOfferId?: string
  bundleOfferId?: string
  onSelect?: (duration: number) => void
  rooms?: Array<App.RoomOccupants>
  saleUnit: string
  displayMemberPrice?: boolean
}

function OfferDurationOptions(props: Props) {
  const {
    durationOptions,
    onClose,
    selectedDuration,
    bundledWithFlightsOnly,
    currentOfferId,
    bundleOfferId,
    rooms,
    saleUnit,
    onSelect = noop,
    displayMemberPrice,
  } = props
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()

  const name = useId()

  const onDurationClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const duration = parseInt(e.currentTarget.dataset.duration!, 10)
    dispatch({ type: OfferPageActions.setDuration, duration, offerId: currentOfferId })
    onClose?.()
    onSelect(duration)
  }, [dispatch, currentOfferId, onClose, onSelect])
  return <>
    {durationOptions.map(option => {
      const price = displayMemberPrice && option.lowestMemberPrice ? option.lowestMemberPrice : option.lowestPrice
      const roundedLowestPrice = price ? Math.ceil(price) : price
      return <ClickableListItem
        key={option.duration}
        title={intl.formatMessage({
          id: 'ZN9-HTVegsVlxrMI9XS4',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Duration option title showing night count on the offer detail page',
        }, { count: option.duration })}
        subtitle={getSubtitleLabel(option, intl)}
        data-duration={option.duration}
        onClick={onDurationClick}
        startIcon={<RadioButton name={name} value={option.duration} checked={option.duration === selectedDuration} />}
        disabled={option.soldOut}
      >
        <ListItemPrice
          price={(option.soldOut || bundledWithFlightsOnly || bundleOfferId) ? undefined : roundedLowestPrice}
          label={bundledWithFlightsOnly || bundleOfferId ? undefined : getOptionLabel(option, rooms ? rooms.length : 1, saleUnit, intl)}
          priceColour={displayMemberPrice ? 'lux-plus' : undefined}
        />
      </ClickableListItem> },
    )}
  </>
}

export default withOfferPageAndReduxState((state, appState) => {
  const currentOfferId = state.paginator?.currentOfferId
  const currentState = takeOfferState(state, currentOfferId)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  const baseDurationOptions = isMultiRoom ? getMultiRoomDurationOptions(currentState, appState) : getDurationOptions(state, appState)
  const durationOptions = (currentState.charterBundleRoute && !currentState.charterBundleEnabled)
    ? filterCharterUnbundledDurationOptions(baseDurationOptions)
    : baseDurationOptions

  return {
    selectedDuration: currentState.duration,
    bundleOfferId: currentState.bundleOffer?.id,
    currentOfferId,
    displayMemberPrice: state.displayMemberPrice,
    durationOptions,
    bundledWithFlightsOnly: currentState.offer?.bundledWithFlightsOnly,
    rooms: isMultiRoom ? state.rooms : undefined,
    saleUnit: state.offer?.saleUnit ?? 'room',
  }
}, OfferDurationOptions)
